import { useState, useEffect } from 'react';
import Footer from './components/Footer/footer';
import LandingPage from './pages/LandingPage/landingPage';
import VerifyEmailPage from './pages/VerifyEmail/verifyEmail';
import FaqPage from './pages/Faq/faq';
import TermsAndConditionsPage from './pages/TermsAndConditions/termsAndConditions';
import { color } from './themes/color';
import Navbar from './components/Navbar/navbar';
import DownloadAppModal from './components/DownloadAppModal/DownloadAppModal';
import { initClickTracking, trackPageView } from './utils/analytics';

// NOTE: /verify-email is a deep-linkable route (opened from email links).
// The hosting layer must be configured to serve index.html for all unknown
// paths (SPA fallback), otherwise direct navigation will 404.
// E.g. Nginx: try_files $uri /index.html;  Vercel/Netlify: rewrites config.
function currentPath(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname.replace(/\/+$/, '') || '/';
}

const path = currentPath();
const isVerifyEmailRoute = path === '/verify-email';
const isFaqRoute = path === '/faq';
const isTermsRoute = path === '/contractterms';

const navbarLinks = isTermsRoute
  ? []
  : isFaqRoute
    ? [{ label: 'Home', href: '/' }]
    : [
        { label: 'How it Works', href: '#how-it-works' },
        { label: 'Partners', href: '#partners' },
      ];

function App() {
  const [modalOpen, setModalOpen] = useState(false);

  const openModal = (): void => setModalOpen(true);
  const closeModal = (): void => setModalOpen(false);
  const navbarCtaOnClick = isTermsRoute ? undefined : openModal;

  useEffect(() => {
    const cleanup = initClickTracking();
    trackPageView(window.location.pathname);
    return cleanup;
  }, []);

  useEffect(() => {
    const scrollToHash = () => {
      const hash = window.location.hash.slice(1);
      if (hash) {
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    };

    scrollToHash();
    window.addEventListener('hashchange', scrollToHash);
    return () => window.removeEventListener('hashchange', scrollToHash);
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: color.cream['100'],
      }}
    >
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
      {!isVerifyEmailRoute && (
        <Navbar
          links={navbarLinks}
          ctaOnClick={navbarCtaOnClick}
          logoHref={isTermsRoute ? null : '/'}
        />
      )}
      <main
        key={path}
        style={{
          flex: 1,
          animation: 'fadeIn 0.4s ease-in',
        }}
      >
        {isVerifyEmailRoute ? (
          <VerifyEmailPage />
        ) : isFaqRoute ? (
          <FaqPage />
        ) : isTermsRoute ? (
          <TermsAndConditionsPage />
        ) : (
          <LandingPage onDownloadClick={openModal} />
        )}
      </main>
      {!isVerifyEmailRoute && !isTermsRoute && (
        <Footer isFaqPage={isFaqRoute} />
      )}
      <DownloadAppModal open={modalOpen} onClose={closeModal} />
    </div>
  );
}

export default App;
